import type { DraftPlayerRegion } from 'types/app'

/**
 * Region Teams helpers shared by the tournament page, join flow and Region
 * Builder. Pure functions only (unit tested in tests/regionTeams.test.ts).
 */

const idOf = (v: any): string => (v === null || v === undefined ? '' : String(v?._id ?? v))

/** Region Teams tournaments keep play_format "captain_pick" and set draft_settings.team_formation. */
export const isRegionTournament = (t: any): boolean =>
  !!t &&
  t.play_format === 'captain_pick' &&
  (t.draft_settings?.team_formation === 'region' || t.team_formation === 'region')

export const draftStatusOf = (t: any): string => t?.draft_settings?.draft_status || 'waiting'

export const isRegionBuilding = (t: any): boolean => isRegionTournament(t) && draftStatusOf(t) === 'region_building'

export const isRegionPublished = (t: any): boolean => isRegionTournament(t) && draftStatusOf(t) === 'completed'

/** "Region teams · 5v5" style label for format cells. */
export const teamFormationLabel = (t: any): string | null => {
  if (!t || t.play_format !== 'captain_pick') return null
  return isRegionTournament(t) ? 'Region teams' : 'Captain draft'
}

/** The viewer's own signup snapshot (players only receive their own entry). */
export const myRegionSnapshot = (t: any, userId?: string | null): DraftPlayerRegion | null => {
  if (!userId) return null
  const list: DraftPlayerRegion[] = t?.draft_player_regions || []
  return list.find((r) => idOf(r.userId) === String(userId)) || null
}

export const containsUser = (list: any[] | undefined, userId?: string | null): boolean =>
  !!userId && (list || []).some((p) => idOf(p) === String(userId))

/* -------------------------------------------------------------------------- */
/* API error codes → user-facing copy                                          */
/* -------------------------------------------------------------------------- */

export type ApiErrorBody = {
  success?: false
  code?: string
  message?: string
  nextChangeAt?: string
  opIndex?: number
  [k: string]: any
}

export const formatShortDate = (iso?: string | Date | null): string => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

const MESSAGES: Record<string, string | ((body: ApiErrorBody) => string)> = {
  // Location
  LOCATION_COOLDOWN: (b) =>
    b.nextChangeAt ? `You can change your location again on ${formatShortDate(b.nextChangeAt)}.` : 'You changed your location recently. Try again later.',
  INVALID_COUNTRY: 'Pick a country from the list.',
  STATE_REQUIRED: 'Pick your state.',
  INVALID_STATE: 'Pick a state from the list.',
  REASON_REQUIRED: 'Add a short reason (at least 3 characters).',
  // Join
  LOCATION_REQUIRED: 'Set your location before joining a region teams tournament.',
  INTERNATIONAL_NOT_ALLOWED: 'This tournament is open to US players only.',
  COMPETITION_ENTRY_BLOCKED: 'Your account can’t enter competitions right now.',
  DRAFT_FULL: 'This tournament is full.',
  // Region Builder
  PLAN_VERSION_CONFLICT: 'Another organizer changed the plan. Reload to see the latest version.',
  PLAN_LOCKED: 'The plan is locked for review. Unlock it to edit.',
  PLAN_NOT_LOCKED: 'The plan isn’t locked.',
  ALREADY_PUBLISHED: 'Teams have already been published.',
  NOT_REGION_BUILDING: 'Registration has to be closed before you can do that.',
  ALREADY_BUILDING: 'Registration is already closed.',
  PLAN_INVALID: 'Fix the validation errors before publishing.',
  EXPECTED_VERSION_REQUIRED: 'Reload the plan and try again.',
  INVALID_TEAM_SIZE: 'Team size must be between 2 and 10.',
  INVALID_SEED: 'Seed must be a whole number.',
  TEAM_NOT_FOUND: 'That team no longer exists. Reload the plan.',
  PLAYER_NOT_IN_PLAN: 'That player is no longer in the plan. Reload the plan.',
  PLAYER_NOT_ON_TEAM: 'That player isn’t on this team.',
  CAPTAIN_NOT_ON_TEAM: 'The captain has to be a starter on that team.',
  INVALID_NAME: 'Team names must be 1–60 characters.',
  PLAYER_NOT_REGISTERED: 'That player has no signup snapshot.',
  NOT_REGION_TOURNAMENT: 'This isn’t a region teams tournament.',
  REGION_SETTINGS_LOCKED: 'Region settings can’t change after teams are published.',
  REGION_MODE_NO_PICKS: 'Region teams are built by the organizer, not drafted.',
}

/** Map an API error body (or axios error) to a clear message. */
export const regionErrorMessage = (errOrBody: any, fallback = 'Something went wrong. Please try again.'): string => {
  const body: ApiErrorBody | undefined = errOrBody?.response?.data ?? errOrBody
  const code = body?.code
  if (code && MESSAGES[code]) {
    const m = MESSAGES[code]
    return typeof m === 'function' ? m(body!) : m
  }
  if (typeof body?.message === 'string' && body.message) return body.message
  if (errOrBody?.code === 'ECONNABORTED' || errOrBody?.name === 'CanceledError') return 'The request timed out. Try again.'
  return fallback
}

export const errorCodeOf = (err: any): string | undefined => err?.response?.data?.code

/* -------------------------------------------------------------------------- */
/* Sub suggestions (D9)                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Order the sub pool by the backend's per-request suggestion (same region
 * first). Players the backend didn't rank keep their original order after the
 * ranked ones. Returns the pool items with their rank (-1 = unranked).
 */
export const rankSubPool = <T,>(pool: T[], suggestion: string[] | undefined): { player: T; rank: number }[] => {
  const order = new Map((suggestion || []).map((id, i) => [String(id), i]))
  return pool
    .map((player, i) => ({ player, rank: order.has(idOf(player)) ? (order.get(idOf(player)) as number) : -1, i }))
    .sort((a, b) => {
      if (a.rank === -1 && b.rank === -1) return a.i - b.i
      if (a.rank === -1) return 1
      if (b.rank === -1) return -1
      return a.rank - b.rank
    })
    .map(({ player, rank }) => ({ player, rank }))
}
